import { useState } from "react"
import { Currency } from "../utils/currencyToSymbolMapper"
import { useTranslation } from "react-i18next"
import { useModal } from "@surveying-hub-bv/fe-component-library"
import { SelectDropdown } from "./Forms"
import { Button } from "./Buttons"
import { PricingTableCard, PricingTableCardsWrapper } from "./Pricing"

// =============================================
// ======== Subscription Pricing Tables ========
// =============================================
type PricingTableSubscriptionProduct = {
    name: string
    priceId: string
    amount: number
    currency: Currency
    bestChoice?: boolean
    attributes?: []
    testMode: boolean
    interval: 'YEARLY' | 'MONTHLY' | 'WEEKLY'
}

type PricingTableProductProps = {
    name: string
    products: PricingTableSubscriptionProduct[]
    createCheckoutSession: (priceId: string) => Promise<string>
}
export const ProductsPricingTableDialog = ({ name, products, createCheckoutSession }: PricingTableProductProps) => {
    const { t } = useTranslation()

    return (
        <div className="modal-content modal-content--large">

            <h3 className="modal-title"><span className="dark:text-darkMode-primary">{name}</span> {t("pricing.subPricingTableTitle")}</h3>
            <div className="overflow-y-auto max-h-[450px] md:max-h-[initial]">
                {/* Note: the below <p> will become dynamic once the user can create their own custom tables. */}
                <p className="dark:text-darkMode-secondaryText mb-6">{t("pricing.subPricingTableCopy")}</p>

                {/* Pricing Table Component */}
                <PricingTableCardsWrapper productLength={products.length}>
                    {products
                        .sort((a, b) => a.amount - b.amount)
                        .map((product, index) => (
                            <PricingTableCard
                                key={index}
                                name={product.name}
                                priceId={product.priceId}
                                createCheckoutSession={createCheckoutSession}
                                type="Subscription"
                                interval={product.interval}
                                amount={product.amount}
                                currency={product.currency}
                                bestChoice={product.bestChoice}
                                attributes={product.attributes}
                                testMode={product.testMode}
                                customClassName="mx-auto w-full"
                            />
                        ))}
                </PricingTableCardsWrapper>
            </div>


        </div>
    )
}

type PricingTableSubscription = {
    name: string, data: PricingTableSubscriptionProduct[]
}

type SelectPricingTableSubscriptionDialogProps = {
    pricingTables: PricingTableSubscription[]
    createCheckoutSession: (priceId: string) => Promise<string>
}
export const SelectPricingTableSubscriptionDialog = ({ pricingTables, createCheckoutSession }: SelectPricingTableSubscriptionDialogProps) => {
    const { showModal, hideModal } = useModal()
    const { t } = useTranslation()
    const [selected, setSelected] = useState<PricingTableSubscription>();
    return <div className="modal-content">
        <h3 className="modal-title">{t("marketplace.sidebar.selectPricingPlanDialog.title")}</h3>
        <p className="dark:text-darkMode-secondaryText">{t("marketplace.sidebar.selectPricingPlanDialog.desc")}</p>

        <SelectDropdown<PricingTableSubscription>
            options={pricingTables.map(pt => ({ label: pt.name, value: pt }))}
            value={selected}
            onChange={setSelected}
            useIndexAsValue
            placeholder={t("marketplace.sidebar.selectPricingPlanDialog.placeholder")}
        />

        <div className="modal-footer-actions">
            <Button label={t("common.cancel")} onClick={hideModal} buttonStyle="muted" size="large" />
            {selected === undefined ?
                <Button fauxButton disabled size="large" label={t("common.select")} /> :
                <Button label={t("common.select")} onClick={() => {
                    showModal(<ProductsPricingTableDialog name={selected.name} products={selected.data} createCheckoutSession={createCheckoutSession} />)
                }} size="large" />
            }
        </div>
    </div>
}

// =============================================
// ========== Credits Pricing Tables ===========
// =============================================
export type PricingTableCreditsProduct = {
    name: string
    priceId: string
    amount: number
    currency: Currency
    creditsAmount: number
    bestChoice: boolean
    attributes: string[]
    testMode: boolean
}

export type PricingTableCreditsDialogProps = {
    products: PricingTableCreditsProduct[]
    createCheckoutSession: (priceId: string) => Promise<string>
}
export const PricingTableCreditsDialog = ({ products, createCheckoutSession }: PricingTableCreditsDialogProps) => {
    const { t } = useTranslation()

    return (
        <div className="modal-content modal-content--large">
            <h3 className="modal-title">{t("subscriptions.dialogs.creditsPricingTable.title")}</h3>
            <div className="overflow-y-auto max-h-[450px] md:max-h-[initial]">
                <p className="dark:text-darkMode-secondaryText mb-6">{t("subscriptions.dialogs.creditsPricingTable.desc")}</p>
                {/* Pricing Table Component */}
                <PricingTableCardsWrapper productLength={products.length}>
                    {products
                        .sort((a, b) => a.amount - b.amount)
                        .map((product, index) => (
                            <PricingTableCard
                                key={index}
                                name={product.name}
                                priceId={product.priceId}
                                createCheckoutSession={() => createCheckoutSession(product.priceId)}
                                type="Token"
                                amount={product.amount}
                                creditsAmount={product.creditsAmount}
                                currency={product.currency}
                                bestChoice={product.bestChoice}
                                attributes={product.attributes}
                                testMode={product.testMode}
                                customClassName="mx-auto w-full"
                            />
                        ))}
                </PricingTableCardsWrapper>
            </div>
        </div>
    )
}

type SelectPricingTableCreditsDialogProps = {
    pricingTables: {name:string, data:PricingTableCreditsProduct[]}[]
    createCheckoutSession: (priceId: string) => Promise<string>
}
export const SelectPricingTableCreditsDialog = ({ pricingTables, createCheckoutSession }: SelectPricingTableCreditsDialogProps) => {
    const { showModal, hideModal } = useModal()
    const { t } = useTranslation()
    const [selected, setSelected] = useState<{ name: string, data: PricingTableCreditsProduct[] }>();
    return <div className="modal-content">
        <h3 className="modal-title">{t("ntripAccounts.dialogs.selectPricingPlanDialog.title")}</h3>
        <p className="dark:text-darkMode-secondaryText">{t("ntripAccounts.dialogs.selectPricingPlanDialog.desc")}</p>

        <SelectDropdown<{ name: string, data: PricingTableCreditsProduct[] }>
            options={pricingTables.map(pt => ({ label: pt.name, value: pt }))}
            value={selected}
            onChange={setSelected}
            useIndexAsValue
            placeholder={t("ntripAccounts.dialogs.selectPricingPlanDialog.placeholder")}
        />

        <div className="modal-footer-actions">
            <Button label={t("common.cancel")} onClick={hideModal} buttonStyle="muted" size="large" />
            {selected === undefined ?
                <Button fauxButton disabled size="large" label={t("common.select")} /> :
                <Button label={t("common.select")} onClick={() => {
                    showModal(<PricingTableCreditsDialog products={selected.data.map(product => ({ ...product, currency: product.currency as Currency }))} createCheckoutSession={createCheckoutSession} />)
                }} size="large" />
            }
        </div>
    </div>
}

